import { ChatVerAction, ChatConfirmAction } from '../../types/chat';
import { ChatService } from './chatService';
import { buildSearchParamsFromFilters, AnimalFiltersQS } from './parseFiltersFromSearchParams';

/**
 * selectionService — ejecuta las acciones que el chatbot envía al frontend.
 *
 * El chat ya vive dentro de la SPA (ChatWidgetContainer), así que la navegación
 * es directa con React Router (useNavigate). Las caravanas grandes nunca van por
 * URL: el modo 'selection_id' las trae del backend y las pasa por sessionStorage,
 * reutilizando el patrón existente de `pending_alert_animals` en AnimalesPage.
 */

export const CHATBOT_SELECTION_KEY = 'pending_chatbot_selection';

/**
 * Evento que se dispara al escribir una nueva selección del chatbot en sessionStorage.
 * Lo escucha AnimalesPage para reaplicar la pre-selección cuando el usuario YA está en
 * /animals: en ese caso navigate('/animals') no remonta el componente y el effect de
 * lectura (que corre solo en mount) no se vuelve a ejecutar.
 */
export const CHATBOT_SELECTION_EVENT = 'chatbot:selection';

/**
 * Payload dejado por el botón "Ver venta/compra {etiqueta}" del chatbot. Contiene las
 * caravanas (resueltas desde el selection_id, igual que el modo 'selection_id'), el
 * `op` ('compra'|'venta') y el `prefill` con los datos de la operación pre-calculados.
 * Lo consume AnimalesPage para abrir SaleModal/PurchaseModal ya sembrado.
 */
export const CHATBOT_OPERACION_KEY = 'pending_chatbot_operacion';

/**
 * Evento que se dispara al escribir un nuevo payload de operación del chatbot en
 * sessionStorage. Análogo a CHATBOT_SELECTION_EVENT: lo escucha AnimalesPage para
 * reaplicar la apertura del modal cuando el usuario YA está en /animals.
 */
export const CHATBOT_OPERACION_EVENT = 'chatbot:operacion';

/**
 * Evento que se dispara al aplicar filtros del chatbot (modo 'filtro'). El detalle
 * lleva el objeto de filtros (AnimalFiltersQS). Lo escucha AnimalesPage para aplicar
 * los filtros cuando el usuario YA está en /animals (navigate a la misma ruta no
 * remonta el componente y el effect de mount no se vuelve a ejecutar).
 */
export const CHATBOT_FILTERS_EVENT = 'chatbot:filters';

type NavigateFn = (path: string) => void;

/** Ejecuta una acción de "ver" (abrir la pantalla destino con los animales). */
export async function executeVerAction(action: ChatVerAction, navigate: NavigateFn): Promise<void> {
  const sel = action.seleccion;

  // Operación pre-cargada desde el chatbot (botón "Ver venta/compra {etiqueta}").
  // Resolvemos las caravanas (si hay selection_id), dejamos el prefill en
  // sessionStorage y disparamos el evento que AnimalesPage escucha para abrir el
  // modal (SaleModal/PurchaseModal) ya sembrado con los datos. Va antes del switch
  // de `modo` porque es un flujo distinto (no queremos caer en 'selection_id').
  if (sel.op && sel.prefill) {
    let caravanas: string[] = [];
    if (sel.selection_id) {
      try {
        const res = await ChatService.getSelection(sel.selection_id);
        caravanas = res.caravanas;
      } catch {
        caravanas = [];
      }
    }
    sessionStorage.setItem(
      CHATBOT_OPERACION_KEY,
      JSON.stringify({ caravanas, prefill: sel.prefill, op: sel.op })
    );
    // Avisar a AnimalesPage si ya está montada en /animals (navigate a la misma
    // ruta no remonta el componente). Si no está montada todavía, el evento se
    // pierde y alcanza con la lectura del effect de mount.
    window.dispatchEvent(new CustomEvent(CHATBOT_OPERACION_EVENT));
    navigate(`/animals?open_operacion=${sel.op}`);
    return;
  }

  switch (sel.modo) {
    case 'filtro': {
      const filtro = (sel.filtro as AnimalFiltersQS) || {};
      const qs = buildSearchParamsFromFilters(filtro);
      const q = qs.toString();
      navigate(`/animals${q ? `?${q}` : ''}`);
      // Avisar a AnimalesPage si ya está montada en /animals (navigate a la misma
      // ruta no remonta el componente). Pasamos los filtros en el detail para no
      // depender de clausuras de location obsoletas.
      window.dispatchEvent(new CustomEvent(CHATBOT_FILTERS_EVENT, { detail: filtro }));
      break;
    }
    case 'session':
      navigate(`/sesiones/${encodeURIComponent(sel.session_code || '')}`);
      break;
    case 'historico':
      navigate(
        `/animals/historical${sel.eventos && sel.eventos.length ? `?evento_id=${encodeURIComponent(sel.eventos[0])}` : ''}`
      );
      break;
    case 'operacion':
      navigate(`/operaciones-compra-venta${sel.op_id ? `?op_id=${encodeURIComponent(sel.op_id)}` : ''}`);
      break;
    case 'selection_id':
    default: {
      if (!sel.selection_id) return;
      const { caravanas } = await ChatService.getSelection(sel.selection_id);
      sessionStorage.setItem(CHATBOT_SELECTION_KEY, JSON.stringify(caravanas));
      // Avisar a AnimalesPage si ya está montada en /animals (navigate a la misma
      // ruta no remonta el componente). Si no está montada todavía (venimos de otra
      // ruta), el evento se pierde y alcanza con la lectura del effect de mount.
      window.dispatchEvent(new CustomEvent(CHATBOT_SELECTION_EVENT));
      navigate('/animals');
      break;
    }
  }
}

/** Ejecuta una acción de "confirmar" (aplica la operación pendiente drift-free).
 *  Con sessionId el backend avisa al agente de esa conversación (incidente 2026-10-31). */
export async function executeConfirmAction(
  action: ChatConfirmAction,
  sessionId?: string
): Promise<{ success: boolean; simulated?: boolean; result: unknown }> {
  return ChatService.confirmOperation(action.pending_op_id, sessionId);
}

/** Lee (y consume) la selección dejada por el chatbot para pre-seleccionar animales. */
export function consumeChatbotSelection(): string[] | null {
  try {
    const raw = sessionStorage.getItem(CHATBOT_SELECTION_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CHATBOT_SELECTION_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
